import {ObjectId} from 'bson';
import {
  GetEnrichedFieldCount,
  GetIsEnrichingAgent,
  GetNumAccountsToEnrichDefinition,
  RunEnrichmentDefinition,
} from '../../shared/api/definitions/root';
import {createApiQuery, useApiMutation} from './api';
import {useQueryClient} from '@tanstack/react-query';
import {EnrichmentPreviewResultQueryKeys} from './fieldDefinitionPreview';
import {AccountSelection} from '../../shared/api/api';
import {EnrichmentMode} from '../../shared/fields/enrichment';

export const IsEnrichingAgentQueryKeys = {
  all: ['isEnrichingAgent'] as const,
  agent: (agentId: ObjectId) =>
    [...IsEnrichingAgentQueryKeys.all, agentId.toString()] as const,
};
export const useIsEnrichingAgent = createApiQuery(
  GetIsEnrichingAgent,
  ({agentId}) => IsEnrichingAgentQueryKeys.agent(agentId),
  {
    refetchInterval: (query) => {
      return query.state.data ? 1_000 : 0;
    },
  }
);

const NumAccountsToEnrichQueryKeys = {
  all: ['numAccountsToEnrich'] as const,
  accountSelection: (
    accountSelection: AccountSelection,
    fieldDefinitionId: ObjectId,
    enrichmentMode: EnrichmentMode,
    preview: boolean | undefined
  ) =>
    [
      ...NumAccountsToEnrichQueryKeys.all,
      fieldDefinitionId.toString(),
      JSON.stringify(accountSelection),
      JSON.stringify(enrichmentMode),
      preview ?? false,
    ] as const,
};

export const useNumAccountsToEnrich = createApiQuery(
  GetNumAccountsToEnrichDefinition,
  ({accountSelection, fieldDefinitionId, enrichmentMode, preview}) =>
    NumAccountsToEnrichQueryKeys.accountSelection(
      accountSelection,
      fieldDefinitionId,
      enrichmentMode,
      preview
    )
);

export const useRunEnrichment = () => {
  const queryClient = useQueryClient();

  return useApiMutation(RunEnrichmentDefinition, {
    onSuccess: async () => {
      // can't surgically invalidate the keys for the field definition because there might be depdendent
      // fields that also end up getting run
      await queryClient.invalidateQueries({
        queryKey: IsEnrichingAgentQueryKeys.all,
      });
      await queryClient.invalidateQueries({
        queryKey: EnrichmentPreviewResultQueryKeys.all,
      });
    },
  });
};

export const EnrichedFieldCountQueryKeys = {
  all: ['enrichedFieldCount'] as const,
  field: (fieldDefinitionId: ObjectId) =>
    [...EnrichedFieldCountQueryKeys.all, fieldDefinitionId.toString()] as const,
};

export const useEnrichedFieldCount = createApiQuery(
  GetEnrichedFieldCount,
  ({fieldDefinitionId}) => EnrichedFieldCountQueryKeys.field(fieldDefinitionId)
);
